import { PositionT } from "../../../domain/entities/map-entities";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import bbox from "@turf/bbox";
import { isEqual } from "lodash";
import { renderToStaticMarkup } from "react-dom/server";
import { BBox, lineString } from "@turf/helpers";
import { EdgeIcon, StopIcon } from "../assets";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type MapProps = {
  route: Array<PositionT>;
  mapId: string;
  noInteractive?: boolean;
};

const useMapBoundingBox = (
  route: Array<PositionT>,
  map?: TrimbleMaps.Map
): (() => void) => {
  const boundingBox: BBox = useMemo(
    () =>
      bbox(
        lineString(
          route.map((stop: PositionT) => [stop.longitude, stop.latitude])
        )
      ),
    [route]
  );

  return useCallback(() => {
    if (map !== undefined) {
      map.fitBounds(
        new TrimbleMaps.LngLatBounds(
          [boundingBox[0], boundingBox[1]],
          [boundingBox[2], boundingBox[3]]
        ),
        { padding: 80 }
      );
    }
  }, [boundingBox, map]);
};

const MapComponent: React.FunctionComponent<MapProps> = (props) => {
  const { route, mapId = "map", noInteractive } = props;

  const [loading, setLoading] = useState<boolean>(true);

  const map = useRef<TrimbleMaps.Map>();
  const mapRoute = useRef<TrimbleMaps.Route>();
  const mapMarkers = useRef<Array<TrimbleMaps.Marker>>([]);
  const centerMap = useMapBoundingBox(route, map.current);

  const removeCurrentMarkets = useCallback(() => {
    mapMarkers.current.forEach((marker) => marker.remove());
    mapMarkers.current = [];
  }, []);

  const buildMap = useCallback(() => {
    removeCurrentMarkets();

    if (route.length) {
      const routeOptions = {
        routeId: `${mapId}Route`,
        routeColor: "#F54D6B",
        stops: route.map((stop, index) => {
          const stopPoints: [number, number] = [stop.longitude, stop.latitude];

          // This object has to be mutable to add the stop icon to trimble map as an HTMLElement
          const mutable_iconContainer = document.createElement("div");
          mutable_iconContainer.innerHTML = renderToStaticMarkup(
            <StopIcon stop={index + 1} fill="#F54D6B" textColor="white" />
          );

          const stopMarker = new TrimbleMaps.Marker({
            element: mutable_iconContainer,
          }).setLngLat(stopPoints);

          if (map.current) {
            mapMarkers.current.push(stopMarker.addTo(map.current));
          }

          return new TrimbleMaps.LngLat(...stopPoints);
        }),
        showStops: false,
      };

      if (mapRoute.current) {
        mapRoute.current.update(routeOptions);
      } else {
        mapRoute.current = new TrimbleMaps.Route(routeOptions);

        if (map.current) {
          mapRoute.current.addTo(map.current);
        }
      }

      mapRoute.current.on("route", () => {
        centerMap();
        setLoading(false);
      });

      mapRoute.current.on("error", () => {
        removeCurrentMarkets();
        mapRoute.current = undefined;
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [route, centerMap, mapId, removeCurrentMarkets]);

  useEffect(() => {
    map.current = new TrimbleMaps.Map({
      container: mapId,
      center: new TrimbleMaps.LngLat(-96, 35),
      zoom: 3,
      interactive: !noInteractive,
    });

    return () => {
      map.current?.remove();
      mapMarkers.current = [];
    };
  }, [mapId, noInteractive]);

  useEffect(() => {
    map.current?.on("load", () => buildMap());
    map.current?.on("error", () => setLoading(false));
  }, [buildMap]);

  return (
    <section id={mapId} className="relative h-full w-full">
      {loading ? (
        <section className="absolute left-0 flex h-full w-full items-center justify-center bg-white text-black">
          <EdgeIcon fill="black" />
        </section>
      ) : null}
    </section>
  );
};

export const Map = memo(
  MapComponent,
  (prevProps: MapProps, nextProps: MapProps) =>
    prevProps.mapId === nextProps.mapId &&
    prevProps.noInteractive === nextProps.noInteractive &&
    isEqual(prevProps.route, nextProps.route)
);
